// src/shared/modules/ledgers/list/hooks/useLedgerData.ts
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../../features/auth/axiosConfig";
import { getLedger } from "src/shared/services/servicesv2";
import { ledgerList } from "src/shared/services/interfacev2";

const useLedgerData = (
  currentPage: number, 
  rowsPerPage: number, 
  searchTerm: string, 
  showZeroOutstanding: boolean, 
  sortField?: string,
  sortOrder?: string,
) => {
  const [ledgerRecords, setLedgerRecords] = useState<ledgerList[]>([]);
  const [visibleRecords, setVisibleRecords] = useState<ledgerList[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();
  const [ledgerRecordsAll, setLedgerRecordsAll] = useState<ledgerList[]>([]);

  // Debounce search term
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 1000);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchTerm]);

  // Fetch all ledger records (for dropdown or other uses)
  useEffect(() => {
    api.get("/pos/v2/ledger?pageNumber=1&pageSize=999999").then((res) => setLedgerRecordsAll(res.data.result));
  }, []);

  // Main fetch function with sorting parameters
  const fetchLedgerRecords = useCallback((
    pageNumber: number, 
    pageSize: number, 
    search: string, 
    includeSettled?: boolean,
    sortField?: string,
    sortOrder?: string,
  ) => {
    setLoading(true);
    
    // Pass includeSettled based on showZeroOutstanding toggle
    // If showZeroOutstanding is true, include settled records
    // If false, only show unsettled records
    getLedger(
      pageNumber, 
      pageSize, 
      search, 
      sortField, 
      sortOrder, 
      showZeroOutstanding // This maps to includeSettled in your API
    )
      .then((response) => {
        const data: ledgerList[] = Array.isArray(response.data.result) ? response.data.result : [];
        setLedgerRecords(data);
        setVisibleRecords(data);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalOutstanding(response.data.total_outstanding_balance || 0);
      })
      .catch((err) => {
        console.error("Error fetching ledger:", err);
      })
      .finally(() => setLoading(false));
  }, [showZeroOutstanding]);

  // Fetch records when dependencies change (including sorting)
  useEffect(() => {
    fetchLedgerRecords(
      currentPage + 1,
      rowsPerPage,
      debouncedSearch,
      showZeroOutstanding,
      sortField,
      sortOrder
    );
  }, [currentPage, rowsPerPage, debouncedSearch, showZeroOutstanding, sortField, sortOrder, fetchLedgerRecords]);

  // Function to fetch specific page (for manual refresh)
  const fetchCurrentPageRecords = useCallback(
    async (page = currentPage, size = rowsPerPage, search = debouncedSearch) => {
      setLoading(true);
      try {
        const response = await getLedger(
          page + 1, 
          size, 
          search, 
          sortField,
          sortOrder,
          showZeroOutstanding
        );
        const data = Array.isArray(response.data.result) ? response.data.result : [];
        
        setLedgerRecords(data);
        setVisibleRecords(data);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalOutstanding(response.data.total_outstanding_balance || 0);
        
        return data;
      } catch (err) {
        console.error("Ledger fetch error:", err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [currentPage, rowsPerPage, debouncedSearch, showZeroOutstanding, sortField, sortOrder]
  );

  // Navigation handler
  const handleRowClick = (id: string) => {
    navigate(`/v1/ledger/${id}`, {
      state: { customers: ledgerRecords },
    });
  };

  // Currency formatting helper
  const formatCurrency = (value: number | undefined): string => {
    if (value === undefined) return "0.00";
    const num = Number(value);
    const thirdDecimal = Math.floor(num * 1000) % 10;
    return thirdDecimal >= 5 ? (Math.ceil(num * 100) / 100).toFixed(2) : num.toFixed(2);
  };

  return {
    ledgerRecords,
    visibleRecords,
    totalRecords,
    totalOutstanding,
    loading,
    handleRowClick,
    formatCurrency,
    fetchCurrentPageRecords,
    showZeroOutstanding,
    ledgerRecordsAll,
  };
};

export default useLedgerData;